import { EventType } from '@/models/enums/EventType'
import { Sdk } from '@/sdk/sdk'
import { LocationQuery, RouteParams } from 'vue-router'
import AWLocation from '@/models/Location'
import store from '@/store'
import { getApiLang, onLanguageChange } from '@/i18n/resolveLanguage'

/**
 * Shared, non-mock SDK instance used by the per-card refresh helpers.
 * Rebuilt on language change so the request language — and the SDK's in-memory
 * response cache — track the active API language. A stale instance would keep
 * serving forecasts (e.g. hourly icon phrases) in the previous language.
 * `getHomeData` still constructs its own SDK because it needs `isMock` from the route query.
 */
let sdk = new Sdk(false, getApiLang())
onLanguageChange(() => {
  sdk = new Sdk(false, getApiLang())
})

/** Number of hourly forecast hours fetched on the location view. */
const HOURLY_FORECAST_HOURS = 6
/** Number of daily forecast days fetched on the location view. */
const DAILY_FORECAST_DAYS = 2

export async function getLocation(
  data: Sdk,
  query?: LocationQuery,
  storedLocation?: AWLocation,
  index = 0,
): Promise<AWLocation | undefined> {
  // getting location
  let lbc = undefined
  let location

  let locationKeyParam = undefined
  if (query && query?.key) locationKeyParam = String(query?.key).split('-')

  if (locationKeyParam && !isNaN(Number(locationKeyParam[index]))) {
    // if we have in params locationKey available
    if (!storedLocation?.Key || storedLocation.Key !== locationKeyParam[index]) {
      // if location key from params does not match with current stored location
      location = await data.getLocationByKey(locationKeyParam[index])
    } else {
      // if location key from params matches with stored location
      location = storedLocation
    }
  } else {
    // if no params with location key
    if (storedLocation?.Key) {
      // if already stored location
      location = storedLocation
    } else {
      // if no params or stored location
      lbc = await data.getLocationByIP()
      location = await data.getLocationByKey(lbc?.Key)
    }
  }
  // storing final location
  if (location?.Key) {
    localStorage.setItem('location', JSON.stringify(location))
  }

  return location
}

export async function getHomeData(
  params?: RouteParams | undefined,
  query?: LocationQuery | undefined,
  storedLocation?: AWLocation,
  include = { hourly: true, daily: true },
  locationIndex = 0,
) {
  const isMock = query?.mock === 'true' ? true : false
  // getHomeData keeps a local SDK instance because the mock flag is request-scoped.
  const data = new Sdk(isMock, getApiLang())
  const location = await getLocation(
    data,
    query,
    locationIndex > 0 ? undefined : storedLocation,
    locationIndex,
  )
  const weatherEvents = await data.getWeatherEvents(location)
  const currentStorm = null
  // get current conditions
  const currentConditions = await data.getCurrentConditions(location?.Key, true)
  const daily = include.daily
    ? data.getDailyWeather(location?.Key, DAILY_FORECAST_DAYS)
    : store.state.home.dailyForecast
  const hourly = include.hourly
    ? data.getHourlyForecast(location?.Key, HOURLY_FORECAST_HOURS)
    : store.state.home.hourlyForescast

  if (!location?.Key) {
    throw new Error('Unable to resolve location for weather data')
  }

  if (!currentConditions || !daily || !hourly) {
    throw new Error('Incomplete weather payload received from SDK')
  }

  return await Promise.all([
    data.getMinutecast(location?.Key, location?.TimeZone.GmtOffset),
    currentConditions,
    weatherEvents,
    daily,
    hourly,
    data.getAlertsByKey(location?.Key),
    currentStorm,
    weatherEvents?.some(
      (event) =>
        event.event === EventType.Ice ||
        event.event === EventType.Snow ||
        event.event === EventType.Winter,
    ),
    location,
  ])
}

/**
 * Fetches only current conditions + minutecast for the given location key.
 * Used by the per-component refresh timer — does not alter location or other slices.
 */
export async function getCurrentConditionsData(locationKey: string) {
  const location = store.state.home.location
  const [minutecast, currentConditions] = await Promise.all([
    sdk.getMinutecast(locationKey, location?.TimeZone.GmtOffset),
    sdk.getCurrentConditions(locationKey, true),
  ])
  return { minutecast, currentConditions }
}

/**
 * Fetches the hourly forecast for the given location key.
 * Used by the per-component refresh timer.
 */
export async function getHourlyForecastData(locationKey: string) {
  return sdk.getHourlyForecast(locationKey, HOURLY_FORECAST_HOURS)
}

/**
 * Fetches the daily forecast for the given location key.
 * Used by the per-component refresh timer.
 */
export async function getDailyForecastData(locationKey: string) {
  return sdk.getDailyWeather(locationKey, DAILY_FORECAST_DAYS)
}

export async function getFullscreenMenuData() {
  const location = store.state.home.location
  const daily = sdk.getDailyWeather(location?.Key, 14)
  const hourly = sdk.getHourlyForecast(location?.Key, 72)
  return await Promise.all([daily, hourly])
}
